import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import TextPostCard from "@/components/Post/TextPostCard/TextPostCard";
import PhotoPostCard from "@/components/Post/PhotoPostCard/PhotoPostCard";
import VideoPostCard from "@/components/Post/VideoPostCard/VideoPostCard";
import Loading from "@/components/Loading/Loading";

import api from "@/utils/axios";
import cls from "./PostSingle.module.scss";

const REACTIONS = {
  heart: { emoji: "❤️", label: "Heart" },
  haha: { emoji: "😂", label: "Haha" },
  wow: { emoji: "😮", label: "Wow" },
  sexy: { emoji: "🔥", label: "Sexy" },
  sad: { emoji: "😢", label: "Sad" },
};

const safeStr = (v) => (v == null ? "" : String(v));
const hasText = (v) => safeStr(v).trim().length > 0;

const extFromFile = (file) => {
  const name = safeStr(file?.name);
  const dot = name.lastIndexOf(".");
  if (dot === -1) return "";
  return name.slice(dot + 1).toLowerCase();
};

const getVideoIdFromPost = (post) => {
  const v = post?.videoId;
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object" && v?._id) return v._id;
  return "";
};

const pickUrlFromImageObj = (img) => {
  if (!img) return "";
  return (
    img?.url ||
    img?.thumbUrl ||
    img?.previewUrl ||
    img?.src ||
    img?.rawUrl ||
    img?.variants?.["480"]?.url ||
    img?.variants?.["960"]?.url ||
    img?.variants?.["1440"]?.url ||
    ""
  );
};

async function sniffVideoMeta(file) {
  return new Promise((resolve) => {
    try {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.muted = true;
      el.playsInline = true;

      el.onloadedmetadata = () => {
        URL.revokeObjectURL(el.src);
        const width = el.videoWidth || 0;
        const height = el.videoHeight || 0;
        const orientation =
          width && height && width < height ? "portrait" : "landscape";
        resolve({ width, height, orientation });
      };

      el.onerror = () => {
        try {
          URL.revokeObjectURL(el.src || "");
        } catch {
          // Intentionally ignored: this best-effort cleanup should not block the UI.
        }
        resolve({ width: 0, height: 0, orientation: "landscape" });
      };

      el.src = URL.createObjectURL(file);
    } catch {
      resolve({ width: 0, height: 0, orientation: "landscape" });
    }
  });
}

const PostSingle = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { postId } = useParams();

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [detail, setDetail] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editErr, setEditErr] = useState("");
  const [editOk, setEditOk] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);

  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");

  const post = detail?.post || null;
  const viewer = detail?.viewer || {};
  const reactions = detail?.reactions?.rows || [];

  const postType = safeStr(post?.type).toLowerCase();

  const loadPost = async () => {
    if (!postId) return;
    setLoading(true);
    setLoadErr("");
    try {
      const { data } = await api.get(`/v1/posts/${postId}/detail`);
      setDetail(data || null);
    } catch (e) {
      setLoadErr(
        e?.response?.data?.message || e?.message || t("postSingle.errors.load"),
      );
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  useEffect(() => {
    if (!editOpen || !post) return;

    setEditTitle(safeStr(post?.title || ""));
    setEditDesc(safeStr(post?.description || ""));
    setEditErr("");
    setEditOk("");

    const imgRows = Array.isArray(post?.images) ? post.images : [];
    setExistingImages(
      imgRows.map((img) => ({
        key: safeStr(img?.key || ""),
        url: pickUrlFromImageObj(img),
      })),
    );

    setNewImages([]);
    setImagePreviewUrls([]);
    setVideoFile(null);
    setVideoPreviewUrl("");
  }, [editOpen, post]);

  useEffect(() => {
    return () => {
      try {
        imagePreviewUrls.forEach((u) => u && URL.revokeObjectURL(u));
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
      try {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const presignImagePoster = async (file) => {
    const ext = extFromFile(file) || "jpg";
    const { data } = await api.post("/v1/posts/media/presign-image-poster", {
      ext,
      contentType: file?.type || "image/jpeg",
    });
    return data;
  };

  const presignVideoViaVideos = async (file) => {
    const contentType = file?.type || "video/mp4";
    const filename =
      safeStr(file?.name) || `video.${extFromFile(file) || "mp4"}`;

    const clientMeta = (await sniffVideoMeta(file).catch(() => null)) || {
      width: 0,
      height: 0,
      orientation: "landscape",
    };

    const { data } = await api.post("/v1/videos/presign", {
      contentType,
      filename,
      clientMeta,
    });

    return data;
  };

  const putToS3WithProgress = (uploadUrl, file, onPct, contentTypeOverride) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);

      xhr.setRequestHeader(
        "Content-Type",
        contentTypeOverride || file?.type || "application/octet-stream",
      );

      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        const pct = Math.round((evt.loaded / evt.total) * 100);
        onPct?.(pct);
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve();
        reject(new Error(`Upload failed (${xhr.status})`));
      };

      xhr.onerror = () => reject(new Error("Upload failed (network error)"));
      xhr.send(file);
    });
  };

  const buildVideoMetaForLocalize = () => {
    const blank = {
      en: { title: "", subtitle: "" },
      si: { title: "", subtitle: "" },
      ta: { title: "", subtitle: "" },
      hi: { title: "", subtitle: "" },
    };

    const lang = safeStr(post?.postLanguage || "en");

    blank[lang] = {
      title: hasText(editTitle) ? editTitle.trim() : "",
      subtitle: hasText(editDesc) ? editDesc.trim() : "",
    };

    return blank;
  };

  const renderFeedPost = (postRow) => {
    const type = safeStr(postRow?.type).toLowerCase();
    const hasImages =
      Array.isArray(postRow?.images) && postRow.images.length > 0;
    const hasVideo = !!getVideoIdFromPost(postRow);

    if (type === "video-post" || type === "video")
      return <VideoPostCard post={postRow} />;
    if (type === "photo-post" || type === "photo")
      return <PhotoPostCard post={postRow} />;
    if (type === "text-only" || type === "text")
      return <TextPostCard post={postRow} />;

    if (hasVideo) return <VideoPostCard post={postRow} />;
    if (hasImages) return <PhotoPostCard post={postRow} />;
    return <TextPostCard post={postRow} />;
  };

  const onPickImages = (evt) => {
    const files = Array.from(evt.target.files || []);
    if (!files.length) return;

    setNewImages(files);

    const previews = files.map((f) => URL.createObjectURL(f));
    setImagePreviewUrls(previews);
  };

  const removeExistingImage = (idx) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeNewImage = (idx) => {
    setNewImages((prev) => prev.filter((_, i) => i !== idx));
    setImagePreviewUrls((prev) => {
      const next = [...prev];
      const [removed] = next.splice(idx, 1);
      try {
        if (removed) URL.revokeObjectURL(removed);
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
      return next;
    });
  };

  const onPickVideo = (evt) => {
    const file = evt.target.files?.[0] || null;
    if (!file) return;

    setVideoFile(file);
    setVideoPreviewUrl((prev) => {
      try {
        if (prev) URL.revokeObjectURL(prev);
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
      return URL.createObjectURL(file);
    });
  };

  const submitEdit = async () => {
    if (!postId || !post) return;
    setEditErr("");
    setEditOk("");

    if (!hasText(editTitle) && !hasText(editDesc)) {
      setEditErr(t("postSingle.errors.textRequired"));
      return;
    }

    if (postType === "photo-post") {
      const totalImages = existingImages.length + newImages.length;
      if (totalImages < 1) {
        setEditErr(t("postSingle.errors.photoRequired"));
        return;
      }
      if (totalImages > 20) {
        setEditErr(t("postSingle.errors.photoMax"));
        return;
      }
    }

    if (postType === "video-post") {
      const hasExistingVideo = !!getVideoIdFromPost(post);
      if (!videoFile && !hasExistingVideo) {
        setEditErr(t("postSingle.errors.videoRequired"));
        return;
      }
    }

    setEditBusy(true);

    try {
      const payload = {
        title: hasText(editTitle) ? editTitle.trim() : null,
        description: hasText(editDesc) ? editDesc.trim() : null,
      };

      if (postType === "photo-post") {
        const uploaded = [];

        for (let i = 0; i < newImages.length; i += 1) {
          const f = newImages[i];
          const pres = await presignImagePoster(f);
          await putToS3WithProgress(pres.uploadUrl, f);
          uploaded.push({ key: pres.processedKey });
        }

        const existingPayload = existingImages
          .map((img) => safeStr(img?.key))
          .filter((key) => hasText(key))
          .map((key) => ({ key }));

        payload.images = [...existingPayload, ...uploaded];
      }

      if (postType === "video-post") {
        if (videoFile) {
          const presign = await presignVideoViaVideos(videoFile);
          const videoId = safeStr(presign?.id).trim();
          if (!videoId) throw new Error("Missing video id");

          await putToS3WithProgress(
            presign.url,
            videoFile,
            null,
            presign.contentType || videoFile?.type,
          );

          const meta = buildVideoMetaForLocalize();
          await api.post(`/v1/videos/${videoId}/localize`, { meta });
          await api.post(`/v1/videos/${videoId}/submit`);

          payload.videoId = videoId;
        } else {
          payload.videoId = getVideoIdFromPost(post);
        }
      }

      await api.patch(`/v1/posts/${postId}`, payload);

      setEditOk(t("postSingle.messages.updated"));
      setEditOpen(false);
      await loadPost();
    } catch (e) {
      setEditErr(
        e?.response?.data?.message || e?.message || t("postSingle.errors.save"),
      );
    } finally {
      setEditBusy(false);
    }
  };

  const onDelete = async () => {
    if (!postId) return;
    setDeleteErr("");
    setDeleteBusy(true);

    try {
      await api.delete(`/v1/posts/${postId}`);
      navigate(-1);
    } catch (e) {
      setDeleteErr(
        e?.response?.data?.message ||
          e?.message ||
          t("postSingle.errors.delete"),
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${isSearch ? cls.activeTab : ""}`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            {loadErr ? (
              <div className={cls.feedStateBad}>{loadErr}</div>
            ) : post ? (
              <>
                <section className={cls.postWrap}>
                  {renderFeedPost(post)}
                </section>

                <section className={cls.card}>
                  <div className={cls.cardHeaderRow}>
                    <div className={cls.cardTitle}>
                      {t("postSingle.reactions.title")}
                    </div>
                    <div className={cls.cardMeta}>
                      {t("postSingle.reactions.count", {
                        count: reactions.length,
                      })}
                    </div>
                  </div>

                  {reactions.length === 0 ? (
                    <div className={cls.emptyState}>
                      {t("postSingle.reactions.empty")}
                    </div>
                  ) : (
                    <div className={cls.reactionList}>
                      {reactions.map((row) => {
                        const reaction = REACTIONS[row.type] || {};
                        const name =
                          row?.displayName ||
                          row?.username ||
                          t("postSingle.user");
                        const memberId = safeStr(row?.memberId || "");
                        const href = memberId
                          ? `/lounge/member-profile/${memberId}`
                          : "";

                        return (
                          <div
                            key={`${row.userId}-${row.type}`}
                            className={cls.reactionItem}
                          >
                            <div className={cls.reactionAvatarWrap}>
                              {row.avatarThumbUrl ? (
                                <img
                                  src={row.avatarThumbUrl}
                                  alt={name}
                                  className={cls.reactionAvatar}
                                />
                              ) : (
                                <div className={cls.reactionAvatarFallback}>
                                  {name?.[0] || "M"}
                                </div>
                              )}
                            </div>

                            <div className={cls.reactionMeta}>
                              {href ? (
                                <Link to={href} className={cls.reactionName}>
                                  {name}
                                </Link>
                              ) : (
                                <span className={cls.reactionName}>{name}</span>
                              )}
                              <span className={cls.reactionType}>
                                {reaction.emoji} {reaction.label || row.type}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
                {viewer?.isOwner ? (
                  <section className={cls.card}>
                    <div className={cls.cardHeaderRow}>
                      <div className={cls.cardTitle}>
                        {t("postSingle.edit.title")}
                      </div>
                      <button
                        type="button"
                        className={cls.editToggle}
                        onClick={() => setEditOpen((prev) => !prev)}
                      >
                        {editOpen
                          ? t("postSingle.actions.cancel")
                          : t("postSingle.actions.edit")}
                      </button>
                    </div>

                    {editOpen ? (
                      <div className={cls.form}>
                        <div className={cls.formRow}>
                          <label className={cls.label}>
                            {t("postSingle.form.titleLabel")}
                          </label>
                          <input
                            className={cls.input}
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            placeholder={t("postSingle.form.titlePlaceholder")}
                            maxLength={160}
                            disabled={editBusy}
                          />
                        </div>

                        <div className={cls.formRow}>
                          <label className={cls.label}>
                            {t("postSingle.form.descLabel")}
                          </label>
                          <textarea
                            className={cls.textarea}
                            value={editDesc}
                            onChange={(e) => setEditDesc(e.target.value)}
                            placeholder={t("postSingle.form.descPlaceholder")}
                            rows={4}
                            maxLength={4000}
                            disabled={editBusy}
                          />
                        </div>

                        {postType === "photo-post" ? (
                          <div className={cls.formRow}>
                            <div className={cls.uploadRow}>
                              <div className={cls.uploadMeta}>
                                <div className={cls.uploadTitle}>
                                  {t("postSingle.form.photosTitle")}
                                </div>
                                <div className={cls.uploadHint}>
                                  {t("postSingle.form.photosHint")}
                                </div>
                              </div>

                              <label className={cls.fileBtn}>
                                {t("postSingle.form.addPhotos")}
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={onPickImages}
                                  disabled={editBusy}
                                  className={cls.fileInput}
                                />
                              </label>
                            </div>

                            {existingImages.length ? (
                              <div className={cls.previewGrid}>
                                {existingImages.map((img, idx) => (
                                  <div
                                    key={img.key}
                                    className={cls.previewItem}
                                  >
                                    {img.url ? (
                                      <img
                                        src={img.url}
                                        alt={t("postSingle.form.currentPhoto")}
                                        className={cls.previewImg}
                                      />
                                    ) : (
                                      <div className={cls.previewFallback} />
                                    )}
                                    <button
                                      type="button"
                                      className={cls.previewRemove}
                                      onClick={() => removeExistingImage(idx)}
                                      disabled={editBusy}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : null}

                            {imagePreviewUrls.length ? (
                              <div className={cls.previewGrid}>
                                {imagePreviewUrls.map((url, idx) => (
                                  <div key={url} className={cls.previewItem}>
                                    <img
                                      src={url}
                                      alt={t("postSingle.form.newPhoto")}
                                      className={cls.previewImg}
                                    />
                                    <button
                                      type="button"
                                      className={cls.previewRemove}
                                      onClick={() => removeNewImage(idx)}
                                      disabled={editBusy}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {postType === "video-post" ? (
                          <div className={cls.formRow}>
                            <div className={cls.uploadRow}>
                              <div className={cls.uploadMeta}>
                                <div className={cls.uploadTitle}>
                                  {t("postSingle.form.videoTitle")}
                                </div>
                                <div className={cls.uploadHint}>
                                  {t("postSingle.form.videoHint")}
                                </div>
                              </div>

                              <label className={cls.fileBtn}>
                                {t("postSingle.form.addVideo")}
                                <input
                                  type="file"
                                  accept="video/*"
                                  onChange={onPickVideo}
                                  disabled={editBusy}
                                  className={cls.fileInput}
                                />
                              </label>
                            </div>

                            {videoFile ? (
                              <div className={cls.videoPreview}>
                                <video
                                  className={cls.videoEl}
                                  src={videoPreviewUrl}
                                  controls
                                  playsInline
                                  preload="metadata"
                                />
                              </div>
                            ) : post?.video?.url ? (
                              <div className={cls.videoPreview}>
                                <video
                                  className={cls.videoEl}
                                  src={post.video.url}
                                  controls
                                  playsInline
                                  preload="metadata"
                                />
                              </div>
                            ) : post?.video?.posterUrl ? (
                              <div className={cls.videoPoster}>
                                <img
                                  src={post.video.posterUrl}
                                  alt={t("postSingle.form.currentVideo")}
                                />
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {editErr ? (
                          <div className={cls.formErr}>{editErr}</div>
                        ) : null}
                        {editOk ? (
                          <div className={cls.formOk}>{editOk}</div>
                        ) : null}

                        <div className={cls.actionsRow}>
                          <button
                            type="button"
                            className={cls.saveBtn}
                            onClick={submitEdit}
                            disabled={editBusy}
                          >
                            {editBusy
                              ? t("postSingle.actions.saving")
                              : t("postSingle.actions.save")}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </section>
                ) : null}

                {viewer?.isOwner ? (
                  <section className={cls.card}>
                    <div className={cls.cardHeaderRow}>
                      <div className={cls.cardTitle}>
                        {t("postSingle.delete.title")}
                      </div>
                      {!confirmDelete ? (
                        <button
                          type="button"
                          className={cls.deleteBtn}
                          onClick={() => setConfirmDelete(true)}
                        >
                          {t("postSingle.actions.delete")}
                        </button>
                      ) : null}
                    </div>

                    {confirmDelete ? (
                      <div className={cls.confirmRow}>
                        <div className={cls.confirmText}>
                          {t("postSingle.delete.confirm")}
                        </div>
                        <div className={cls.confirmActions}>
                          <button
                            type="button"
                            className={cls.cancelBtn}
                            onClick={() => setConfirmDelete(false)}
                            disabled={deleteBusy}
                          >
                            {t("postSingle.actions.no")}
                          </button>
                          <button
                            type="button"
                            className={cls.deleteBtn}
                            onClick={onDelete}
                            disabled={deleteBusy}
                          >
                            {deleteBusy
                              ? t("postSingle.actions.deleting")
                              : t("postSingle.actions.yes")}
                          </button>
                        </div>
                        {deleteErr ? (
                          <div className={cls.formErr}>{deleteErr}</div>
                        ) : null}
                      </div>
                    ) : null}
                  </section>
                ) : null}
              </>
            ) : (
              <div className={cls.feedState}>{t("postSingle.empty")}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostSingle;
